import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { StateApiError, checkForBadHttpErrorMessage, checkToAddStateApiError, checkToRemoveStateApiError } from '@trovata/app/core/models/state-error.model';
import { StablecoinTransfersService } from '../../../services/transfers/transfers.service';
import { StablecoinPage } from '../../../models/stablecoin.model';
import {
	NEEDS_ACTION_STATUSES,
	StablecoinApprovalPolicy,
	StablecoinApprovalState,
	StablecoinBulkApprovalResponse,
	StablecoinBulkApprovalResult,
	StablecoinTransfer,
	StablecoinTransferAction,
} from '../../../models/transfer.model';
import {
	BulkDecideStablecoinTransfers,
	CancelStablecoinTransfer,
	ClearStablecoinTransferHold,
	ClearStablecoinTransfersState,
	CreateStablecoinTransfer,
	GetNeedsApprovalTransfers,
	GetStablecoinApprovalPolicy,
	GetStablecoinTransfers,
	GetTransferApprovals,
	InitStablecoinTransfersState,
	ResetStablecoinTransfersState,
	RetryStablecoinTransfer,
	SubmitTransferApproval,
} from '../../actions/transfers.actions';

/** Actions that put a payment in the caller's "Ready for Review" queue. */
const REVIEW_ACTIONS: StablecoinTransferAction[] = [StablecoinTransferAction.approve, StablecoinTransferAction.release, StablecoinTransferAction.clearHold];

/** Is the payment still in the caller's review queue? Server-driven via `my_actions`; legacy APIs fall back to status. */
export function isReadyForReview(transfer: StablecoinTransfer): boolean {
	if (transfer.my_actions) {
		return transfer.my_actions.some((action: StablecoinTransferAction) => REVIEW_ACTIONS.includes(action));
	}
	return NEEDS_ACTION_STATUSES.includes(transfer.status);
}

export interface StablecoinTransfersStateModel {
	transfers: StablecoinTransfer[] | null;
	transfersNextCursor: string | null;
	needsApproval: StablecoinTransfer[] | null;
	approvalsByTransferId: { [transferId: string]: StablecoinApprovalState[] };
	policy: StablecoinApprovalPolicy | null;
	apiErrors: StateApiError[];
	isCached: boolean;
	transfersLoading: boolean;
	needsApprovalLoading: boolean;
	transferActionInFlight: boolean;
	/** Per-item results of the last bulk approve/reject. */
	lastBulkApprovalResults: StablecoinBulkApprovalResult[];
}

const DEFAULT_STABLECOIN_TRANSFERS_STATE: StablecoinTransfersStateModel = {
	transfers: null,
	transfersNextCursor: null,
	needsApproval: null,
	approvalsByTransferId: {},
	policy: null,
	apiErrors: [],
	isCached: false,
	transfersLoading: true,
	needsApprovalLoading: true,
	transferActionInFlight: false,
	lastBulkApprovalResults: [],
};

@State<StablecoinTransfersStateModel>({
	name: 'stablecoinTransfers',
	defaults: { ...DEFAULT_STABLECOIN_TRANSFERS_STATE },
})
@Injectable()
export class StablecoinTransfersState {
	private readonly transfersService: StablecoinTransfersService = inject(StablecoinTransfersService);

	@Selector()
	static transfers(state: StablecoinTransfersStateModel): StablecoinTransfer[] | null {
		return state.transfers;
	}

	@Selector()
	static transfersNextCursor(state: StablecoinTransfersStateModel): string | null {
		return state.transfersNextCursor;
	}

	@Selector()
	static needsApproval(state: StablecoinTransfersStateModel): StablecoinTransfer[] | null {
		return state.needsApproval;
	}

	@Selector()
	static needsApprovalCount(state: StablecoinTransfersStateModel): number {
		return state.needsApproval?.length ?? 0;
	}

	@Selector()
	static approvalsByTransferId(state: StablecoinTransfersStateModel): { [transferId: string]: StablecoinApprovalState[] } {
		return state.approvalsByTransferId;
	}

	@Selector()
	static policy(state: StablecoinTransfersStateModel): StablecoinApprovalPolicy | null {
		return state.policy;
	}

	@Selector()
	static transfersLoading(state: StablecoinTransfersStateModel): boolean {
		return state.transfersLoading;
	}

	@Selector()
	static needsApprovalLoading(state: StablecoinTransfersStateModel): boolean {
		return state.needsApprovalLoading;
	}

	@Selector()
	static transferActionInFlight(state: StablecoinTransfersStateModel): boolean {
		return state.transferActionInFlight;
	}

	@Selector()
	static lastBulkApprovalResults(state: StablecoinTransfersStateModel): StablecoinBulkApprovalResult[] {
		return state.lastBulkApprovalResults;
	}

	@Selector()
	static apiErrors(state: StablecoinTransfersStateModel): StateApiError[] {
		return state.apiErrors;
	}

	@Action(InitStablecoinTransfersState)
	init(): void {
		// Fetches are dispatched lazily from page components (no entitlement gate yet).
	}

	@Action(ClearStablecoinTransfersState)
	clear(context: StateContext<StablecoinTransfersStateModel>): void {
		context.setState({ ...DEFAULT_STABLECOIN_TRANSFERS_STATE });
	}

	@Action(ResetStablecoinTransfersState)
	reset(context: StateContext<StablecoinTransfersStateModel>): void {
		context.setState({ ...DEFAULT_STABLECOIN_TRANSFERS_STATE });
	}

	// cancelUncompleted: a tab/filter switch (or a poll) supersedes an in-flight fetch, so a stale page never lands last.
	@Action(GetStablecoinTransfers, { cancelUncompleted: true })
	getTransfers(context: StateContext<StablecoinTransfersStateModel>, action: GetStablecoinTransfers): Observable<StablecoinPage<StablecoinTransfer>> {
		if (!action.silent) {
			context.patchState({ transfersLoading: true });
		}
		return this.transfersService.getTransfers(action.filter, action.cursor).pipe(
			tap((page: StablecoinPage<StablecoinTransfer>) => {
				checkToRemoveStateApiError(context, GetStablecoinTransfers);
				const state: StablecoinTransfersStateModel = context.getState();
				// Cursor fetches append; fresh fetches replace.
				const transfers: StablecoinTransfer[] = action.cursor ? [...(state.transfers ?? []), ...page.items] : page.items;
				context.patchState({ transfers, transfersNextCursor: page.next_cursor, transfersLoading: false, isCached: true });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ transfersLoading: false });
				checkToAddStateApiError(error, context, GetStablecoinTransfers, checkForBadHttpErrorMessage(error, 'Unable to load transfers.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetNeedsApprovalTransfers, { cancelUncompleted: true })
	getNeedsApproval(context: StateContext<StablecoinTransfersStateModel>, action: GetNeedsApprovalTransfers): Observable<StablecoinPage<StablecoinTransfer>> {
		if (!action.silent) {
			context.patchState({ needsApprovalLoading: true });
		}
		return this.transfersService.getNeedsApprovalTransfers().pipe(
			tap((page: StablecoinPage<StablecoinTransfer>) => {
				checkToRemoveStateApiError(context, GetNeedsApprovalTransfers);
				context.patchState({ needsApproval: page.items, needsApprovalLoading: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ needsApprovalLoading: false });
				checkToAddStateApiError(error, context, GetNeedsApprovalTransfers, checkForBadHttpErrorMessage(error, 'Unable to load pending approvals.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(CreateStablecoinTransfer)
	createTransfer(context: StateContext<StablecoinTransfersStateModel>, action: CreateStablecoinTransfer): Observable<StablecoinTransfer> {
		context.patchState({ transferActionInFlight: true });
		return this.transfersService.createTransfer(action.body, action.idempotencyKey).pipe(
			tap((transfer: StablecoinTransfer) => {
				checkToRemoveStateApiError(context, CreateStablecoinTransfer);
				const state: StablecoinTransfersStateModel = context.getState();
				context.patchState({
					transfers: [transfer, ...(state.transfers ?? [])],
					needsApproval: isReadyForReview(transfer) ? [transfer, ...(state.needsApproval ?? [])] : state.needsApproval,
					transferActionInFlight: false,
				});
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ transferActionInFlight: false });
				checkToAddStateApiError(error, context, CreateStablecoinTransfer, checkForBadHttpErrorMessage(error, 'Unable to create transfer.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(CancelStablecoinTransfer)
	cancelTransfer(context: StateContext<StablecoinTransfersStateModel>, action: CancelStablecoinTransfer): Observable<StablecoinTransfer> {
		context.patchState({ transferActionInFlight: true });
		return this.transfersService.cancelTransfer(action.transferId, action.reason).pipe(
			tap((transfer: StablecoinTransfer) => {
				checkToRemoveStateApiError(context, CancelStablecoinTransfer);
				this.replaceTransfer(context, transfer);
				context.patchState({ transferActionInFlight: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ transferActionInFlight: false });
				checkToAddStateApiError(error, context, CancelStablecoinTransfer, checkForBadHttpErrorMessage(error, 'Unable to cancel transfer.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(RetryStablecoinTransfer)
	retryTransfer(context: StateContext<StablecoinTransfersStateModel>, action: RetryStablecoinTransfer): Observable<StablecoinTransfer> {
		context.patchState({ transferActionInFlight: true });
		return this.transfersService.retryTransfer(action.transferId).pipe(
			tap((retry: StablecoinTransfer) => {
				checkToRemoveStateApiError(context, RetryStablecoinTransfer);
				const state: StablecoinTransfersStateModel = context.getState();
				// The server marks the old payment; mirror it locally so Retry disappears before the next refresh.
				const markRetried = (item: StablecoinTransfer): StablecoinTransfer =>
					item.id === action.transferId ? { ...item, retried_by_transfer_id: retry.id, can_retry: false } : item;
				context.patchState({
					transfers: state.transfers ? [retry, ...state.transfers.map(markRetried)] : [retry],
					needsApproval: isReadyForReview(retry) ? [retry, ...(state.needsApproval ?? [])] : state.needsApproval,
					transferActionInFlight: false,
				});
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ transferActionInFlight: false });
				checkToAddStateApiError(error, context, RetryStablecoinTransfer, checkForBadHttpErrorMessage(error, 'Unable to retry the payment.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(BulkDecideStablecoinTransfers)
	bulkDecide(context: StateContext<StablecoinTransfersStateModel>, action: BulkDecideStablecoinTransfers): Observable<StablecoinBulkApprovalResponse> {
		context.patchState({ transferActionInFlight: true, lastBulkApprovalResults: [] });
		return this.transfersService.bulkApprovals({ transfer_ids: action.transferIds, decision: action.decision, comment: action.comment?.trim() || null }).pipe(
			tap((response: StablecoinBulkApprovalResponse) => {
				checkToRemoveStateApiError(context, BulkDecideStablecoinTransfers);
				context.patchState({ lastBulkApprovalResults: response.results, transferActionInFlight: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ transferActionInFlight: false });
				checkToAddStateApiError(error, context, BulkDecideStablecoinTransfers, checkForBadHttpErrorMessage(error, 'Unable to record the decisions.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetTransferApprovals)
	getTransferApprovals(context: StateContext<StablecoinTransfersStateModel>, action: GetTransferApprovals): Observable<StablecoinApprovalState[]> {
		return this.transfersService.getTransferApprovals(action.transferId).pipe(
			tap((approvalStates: StablecoinApprovalState[]) => {
				checkToRemoveStateApiError(context, GetTransferApprovals);
				context.patchState({
					approvalsByTransferId: { ...context.getState().approvalsByTransferId, [action.transferId]: approvalStates },
				});
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, GetTransferApprovals, checkForBadHttpErrorMessage(error, 'Unable to load approvals.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(SubmitTransferApproval)
	submitTransferApproval(context: StateContext<StablecoinTransfersStateModel>, action: SubmitTransferApproval): Observable<StablecoinTransfer> {
		context.patchState({ transferActionInFlight: true });
		return this.transfersService.submitTransferApproval(action.transferId, action.decision, action.comment).pipe(
			tap((transfer: StablecoinTransfer) => {
				checkToRemoveStateApiError(context, SubmitTransferApproval);
				this.replaceTransfer(context, transfer);
				context.patchState({ transferActionInFlight: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ transferActionInFlight: false });
				checkToAddStateApiError(error, context, SubmitTransferApproval, checkForBadHttpErrorMessage(error, 'Unable to record decision.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetStablecoinApprovalPolicy)
	getPolicy(context: StateContext<StablecoinTransfersStateModel>): Observable<StablecoinApprovalPolicy> {
		return this.transfersService.getApprovalPolicy().pipe(
			tap((policy: StablecoinApprovalPolicy) => {
				checkToRemoveStateApiError(context, GetStablecoinApprovalPolicy);
				context.patchState({ policy });
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, GetStablecoinApprovalPolicy, checkForBadHttpErrorMessage(error, 'Unable to load approval policy.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(ClearStablecoinTransferHold)
	clearTransferHold(context: StateContext<StablecoinTransfersStateModel>, action: ClearStablecoinTransferHold): Observable<StablecoinTransfer> {
		context.patchState({ transferActionInFlight: true });
		return this.transfersService.clearHold(action.transferId, action.comment).pipe(
			tap((transfer: StablecoinTransfer) => {
				checkToRemoveStateApiError(context, ClearStablecoinTransferHold);
				this.replaceTransfer(context, transfer);
				context.patchState({ transferActionInFlight: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ transferActionInFlight: false });
				checkToAddStateApiError(error, context, ClearStablecoinTransferHold, checkForBadHttpErrorMessage(error, 'Unable to clear the hold.').message);
				return throwError(() => error);
			})
		);
	}

	private replaceTransfer(context: StateContext<StablecoinTransfersStateModel>, transfer: StablecoinTransfer): void {
		const state: StablecoinTransfersStateModel = context.getState();
		const mapTransfer = (item: StablecoinTransfer): StablecoinTransfer => (item.id === transfer.id ? transfer : item);
		context.patchState({
			transfers: state.transfers?.map(mapTransfer) ?? null,
			needsApproval: state.needsApproval?.map(mapTransfer).filter(isReadyForReview) ?? null,
		});
	}
}
